var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { useState, useEffect } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { PeriodicalLibraryStatusEnum, } from "../types/Api";
import { DEFAULT_VOLUME_STATUSES, parsePeriodicalStatusesFromUrl, } from "../utils/volumeConstants";
import { INITIAL_LAST_RECEIVED_FILTER, isLastReceivedFilterActive, normalizeLastReceivedFilter, } from "../utils/lastReceivedFilter";
import { parseAgendaState } from "../types/agenda";
var readLastReceivedFromUrl = function (queryParams) {
    var _a;
    return normalizeLastReceivedFilter({
        lastReceivedYear: (_a = queryParams.get("lastReceivedYear")) !== null && _a !== void 0 ? _a : "",
        lastReceivedIncludeOlder: queryParams.get("lastReceivedIncludeOlder") === "true",
        neverReceived: queryParams.get("neverReceived") === "true",
    });
};
export var usePeriodicalFilters = function (_a) {
    var _b = _a === void 0 ? {} : _a, _c = _b.lastReceived, lastReceived = _c === void 0 ? false : _c, _d = _b.agendaState, agendaState = _d === void 0 ? false : _d;
    var history = useHistory();
    var location = useLocation();
    var initialFilters = __assign({ name: "", issn: "", publisherId: "", frequencies: [], kultSubjectGroupOrigins: [], showEnded: false, statuses: [], libraryStatus: PeriodicalLibraryStatusEnum.PENDING, provisional: false, agendaState: "" }, INITIAL_LAST_RECEIVED_FILTER);
    var _e = useState(initialFilters), filters = _e[0], setFilters = _e[1];
    var _f = useState(initialFilters), appliedFilters = _f[0], setAppliedFilters = _f[1];
    var _g = useState(""), publisherQuery = _g[0], setPublisherQuery = _g[1];
    var updateUrlWithFilters = function (newFilters) {
        var params = new URLSearchParams();
        if (newFilters.name)
            params.set("name", newFilters.name);
        if (newFilters.issn)
            params.set("issn", newFilters.issn);
        if (newFilters.publisherId)
            params.set("publisherId", newFilters.publisherId);
        newFilters.frequencies.forEach(function (frequency) {
            return params.append("frequencies", frequency);
        });
        newFilters.kultSubjectGroupOrigins.forEach(function (origin) {
            return params.append("kultSubjectGroupOrigins", origin);
        });
        newFilters.statuses.forEach(function (status) { return params.append("statuses", status); });
        if (newFilters.showEnded)
            params.set("showEnded", "true");
        if (newFilters.libraryStatus !== PeriodicalLibraryStatusEnum.PENDING)
            params.set("libraryStatus", newFilters.libraryStatus);
        if (newFilters.provisional)
            params.set("provisional", "true");
        if (agendaState && newFilters.agendaState)
            params.set("agendaState", newFilters.agendaState);
        if (lastReceived) {
            if (newFilters.lastReceivedYear)
                params.set("lastReceivedYear", newFilters.lastReceivedYear);
            if (newFilters.lastReceivedIncludeOlder)
                params.set("lastReceivedIncludeOlder", "true");
            if (newFilters.neverReceived)
                params.set("neverReceived", "true");
        }
        history.push({ search: params.toString() });
    };
    var handleFilterChange = function (field, value) {
        var _a;
        var newFilters = __assign(__assign({}, filters), (_a = {}, _a[field] = value, _a));
        setFilters(newFilters);
    };
    var handleShowEndedChange = function (showEnded) {
        setFilters(__assign(__assign({}, filters), { showEnded: showEnded }));
    };
    var handleFrequencyChange = function (frequency) {
        var newFrequencies = filters.frequencies.includes(frequency)
            ? filters.frequencies.filter(function (existing) { return existing !== frequency; })
            : __spreadArray(__spreadArray([], filters.frequencies, true), [frequency], false);
        handleFilterChange("frequencies", newFrequencies);
    };
    var handleKultSubjectGroupOriginChange = function (origin) {
        var origins = filters.kultSubjectGroupOrigins;
        handleFilterChange("kultSubjectGroupOrigins", origins.includes(origin)
            ? origins.filter(function (existing) { return existing !== origin; })
            : __spreadArray(__spreadArray([], origins, true), [origin], false));
    };
    var handleStatusChange = function (status) {
        var selected = filters.statuses.includes(status)
            ? filters.statuses.filter(function (existing) { return existing !== status; })
            : __spreadArray(__spreadArray([], filters.statuses, true), [status], false);
        // URL parsing returns list order; click order would make the applied filters differ and refetch
        var newStatuses = DEFAULT_VOLUME_STATUSES.filter(function (candidate) {
            return selected.includes(candidate);
        });
        setFilters(__assign(__assign({}, filters), { statuses: newStatuses }));
    };
    var handleLibraryStatusChange = function (libraryStatus) {
        setFilters(__assign(__assign({}, filters), { libraryStatus: libraryStatus }));
    };
    var handleProvisionalChange = function (provisional) {
        setFilters(__assign(__assign({}, filters), { provisional: provisional }));
    };
    var handleAgendaStateChange = function (value) {
        setFilters(__assign(__assign({}, filters), { agendaState: value }));
    };
    var handleLastReceivedChange = function (change) {
        setFilters(__assign(__assign({}, filters), change));
    };
    var handlePublisherInput = function (inputValue) {
        if (inputValue !== publisherQuery) {
            setPublisherQuery(inputValue);
        }
    };
    var handleApplyFilters = function () {
        var normalizedFilters = __assign(__assign({}, filters), normalizeLastReceivedFilter(filters));
        setFilters(normalizedFilters);
        setAppliedFilters(normalizedFilters);
        updateUrlWithFilters(normalizedFilters);
    };
    var handleClearFilters = function () {
        var clearedFilters = initialFilters;
        setFilters(clearedFilters);
        setAppliedFilters(clearedFilters);
        setPublisherQuery("");
        history.push({ search: "" });
    };
    var isAnyFilterActive = function () {
        return (appliedFilters.name.length > 0 ||
            appliedFilters.issn.length > 0 ||
            appliedFilters.publisherId.length > 0 ||
            appliedFilters.frequencies.length > 0 ||
            appliedFilters.kultSubjectGroupOrigins.length > 0 ||
            appliedFilters.statuses.length > 0 ||
            appliedFilters.showEnded ||
            appliedFilters.libraryStatus !== PeriodicalLibraryStatusEnum.PENDING ||
            appliedFilters.provisional ||
            appliedFilters.agendaState.length > 0 ||
            (lastReceived && isLastReceivedFilterActive(appliedFilters)));
    };
    var hasUnappliedChanges = function () {
        return JSON.stringify(filters) !== JSON.stringify(appliedFilters);
    };
    useEffect(function () {
        var queryParams = new URLSearchParams(location.search);
        var newFilters = __assign({ name: queryParams.get("name") || "", issn: queryParams.get("issn") || "", publisherId: queryParams.get("publisherId") || "", frequencies: queryParams.getAll("frequencies"), kultSubjectGroupOrigins: queryParams.getAll("kultSubjectGroupOrigins"), showEnded: queryParams.get("showEnded") === "true", statuses: parsePeriodicalStatusesFromUrl(queryParams), libraryStatus: queryParams.get("libraryStatus") ||
                PeriodicalLibraryStatusEnum.PENDING, provisional: queryParams.get("provisional") === "true", agendaState: agendaState
                ? parseAgendaState(queryParams.get("agendaState"))
                : "" }, (lastReceived
            ? readLastReceivedFromUrl(queryParams)
            : INITIAL_LAST_RECEIVED_FILTER));
        if (JSON.stringify(newFilters) !== JSON.stringify(appliedFilters)) {
            setFilters(newFilters);
            setAppliedFilters(newFilters);
        }
    }, [location.search, appliedFilters, lastReceived, agendaState]);
    return {
        filters: filters,
        appliedFilters: appliedFilters,
        publisherQuery: publisherQuery,
        handleFilterChange: handleFilterChange,
        handleFrequencyChange: handleFrequencyChange,
        handleKultSubjectGroupOriginChange: handleKultSubjectGroupOriginChange,
        handleStatusChange: handleStatusChange,
        handleLibraryStatusChange: handleLibraryStatusChange,
        handleShowEndedChange: handleShowEndedChange,
        handleProvisionalChange: handleProvisionalChange,
        handleAgendaStateChange: handleAgendaStateChange,
        handleLastReceivedChange: handleLastReceivedChange,
        handlePublisherInput: handlePublisherInput,
        handleApplyFilters: handleApplyFilters,
        handleClearFilters: handleClearFilters,
        isAnyFilterActive: isAnyFilterActive,
        hasUnappliedChanges: hasUnappliedChanges,
    };
};
